import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { checkPasswordPolicy } from '@astrologermatch/shared';
import type {
  ClaimCompleteResponse,
  ClaimRequestOtpResponse,
  ClaimVerifyOtpResponse,
} from '@astrologermatch/shared';
import { SITE_NAME } from '../components/Layout.js';
import { api, ApiError } from '../lib/api.js';
import { useSeo } from '../lib/seo.js';
import { useSession } from '../lib/session.js';

// "Is this you? Claim this profile" (batch: unclaimed profiles §3): request an OTP to the
// admin-entered email (masked, un-editable), verify it, then set a password to become a normal
// astrologer account.

type Step = 'intro' | 'code' | 'password';

export function ClaimProfile({ data }: { data: { slug: string; name: string; claimed: boolean } }) {
  useSeo({ title: `Claim your profile | ${SITE_NAME}`, robots: 'noindex' });
  const navigate = useNavigate();
  const { setSession } = useSession();

  const [step, setStep] = useState<Step>('intro');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [code, setCode] = useState('');
  const [ticket, setTicket] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (data.claimed) {
    return (
      <div className="wrap page-pad">
        <div className="card narrow center-card">
          <h1 className="h2">Already claimed</h1>
          <p className="muted">
            This profile has already been claimed. If that wasn&apos;t you, please contact us.
          </p>
          <Link to={`/astrologers/${data.slug}`} className="btn btn-primary">
            View profile
          </Link>
        </div>
      </div>
    );
  }

  async function requestOtp() {
    setBusy(true);
    setErr('');
    try {
      const res = await api<ClaimRequestOtpResponse>(
        'POST',
        `/api/v1/astrologers/${data.slug}/claim/request-otp`,
      );
      setMaskedEmail(res.maskedEmail);
      setStep('code');
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const res = await api<ClaimVerifyOtpResponse>(
        'POST',
        `/api/v1/astrologers/${data.slug}/claim/verify-otp`,
        { code: code.trim() },
      );
      setTicket(res.ticket);
      setStep('password');
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function complete(e: FormEvent) {
    e.preventDefault();
    setErr('');
    const policy = checkPasswordPolicy(password);
    if (!policy.valid) {
      setErr(policy.errors.join(' '));
      return;
    }
    if (password !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    if (!agree) {
      setErr('Please accept the Terms to continue.');
      return;
    }
    setBusy(true);
    try {
      const res = await api<ClaimCompleteResponse>(
        'POST',
        `/api/v1/astrologers/${data.slug}/claim/complete`,
        { ticket, password, acceptedTerms: true },
      );
      setSession({ role: 'astrologer', name: res.name, csrfToken: res.csrfToken });
      void navigate('/dashboard');
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="wrap page-pad">
      <section className="card narrow center-card auth-card">
        <h1 className="h2">Claim your profile</h1>
        <p className="muted">
          {data.name}, AstrologerMatch built a profile for you from public information. Claim it to
          unlock reviews, promotion and a free website badge.
        </p>
        {err && (
          <div className="alert alert-error" role="alert">
            {err}
          </div>
        )}

        {step === 'intro' && (
          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={busy}
            onClick={() => void requestOtp()}
          >
            {busy ? 'Sending…' : 'Is this you? Send a verification code'}
          </button>
        )}

        {step === 'code' && (
          <form onSubmit={(e) => void verifyOtp(e)}>
            <p className="small muted">
              We sent a 6-digit code to <b>{maskedEmail}</b> — the email on file for this profile.
            </p>
            <label>
              Verification code
              <input
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, ''));
                }}
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={busy || code.length !== 6}
            >
              {busy ? 'Verifying…' : 'Verify code'}
            </button>
            <p className="tiny muted mt16">
              <button
                type="button"
                className="linkish"
                disabled={busy}
                onClick={() => void requestOtp()}
              >
                Resend code
              </button>
              {' · '}
              <a href="mailto:support@astrologermatch.com">
                Can&apos;t access this email? Contact us
              </a>
            </p>
          </form>
        )}

        {step === 'password' && (
          <form onSubmit={(e) => void complete(e)}>
            <label>
              Choose a password
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
              />
            </label>
            <label>
              Confirm password
              <input
                type="password"
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                }}
              />
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => {
                  setAgree(e.target.checked);
                }}
              />{' '}
              I agree to the <Link to="/terms">Terms</Link> and the{' '}
              <Link to="/advertiser-policy">Advertiser Policy</Link>, and confirm I won&apos;t
              promise guaranteed outcomes to clients.
            </label>
            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              {busy ? 'Claiming…' : 'Claim my profile'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
